import {
  type StudentGrade,
  dayMonth,
  gradeLevel,
  pointsAverage,
  shortDate,
  termFor,
  todayIso,
  useStudentRecord,
  useTerms,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import { EmptyState, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  SUBJECT_GRAD,
  SubjectArt,
  Tabs,
  grad,
} from '../design/forms';
import {
  CaretDownIcon,
  ChartBarIcon,
  ChatCircleIcon,
  type FilledIcon,
  HeartIcon,
  LightbulbIcon,
  MedalIcon,
  StarIcon,
} from '../design/icons';
import { gradeLong, gradeShort } from '../lib/labels';
import { useLook } from '../design/looks';
import { ShapesGrades } from '../student/ShapesGrades';
import { StudentGrades } from '../student/StudentGrades';

/*
 * «Оцінки» for the family in «Форми 2» (28.10): the same page the child sees (the average big and
 * light, how the grades spread, the subjects as rows with the grades as marks in their level's
 * colour), and what a parent needs more — the absences with their reason.
 */

/** the colour of a level: low coral, then butter, sky, lime; thematic and term grades lavender */
const LEVEL: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];
const FINAL = new Set<StudentGrade['kind']>(['thematic', 'semester', 'year']);

const toneOf = (g: StudentGrade): Grad =>
  FINAL.has(g.kind) ? 'lavender' : g.value ? (LEVEL[gradeLevel(g.value)] ?? 'stone') : 'stone';

/** the teachers' words by their kind: a colour and a small icon */
const FEEDBACK: Record<string, { tone: Grad; icon: FilledIcon }> = {
  star: { tone: 'butter', icon: StarIcon },
  progress: { tone: 'sky', icon: ChartBarIcon },
  effort: { tone: 'coral', icon: MedalIcon },
  idea: { tone: 'lime', icon: LightbulbIcon },
  help: { tone: 'pink', icon: HeartIcon },
};

/** an absence's reason as a soft mark */
const ABSENCE: Record<string, Grad> = { absent: 'coral', alarm: 'butter', technical: 'stone' };

const decimal = (n: number | null) => (n !== null ? String(n).replace('.', ',') : '—');

/** A grade as a small soft mark on its level's colour (the home and the journal too). */
export function GradeBadge({ grade, size = 'sm' }: { grade: StudentGrade; size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  return (
    <span
      title={`${shortDate(grade.date)} · ${t(`journal.kinds.${grade.kind}`)}${grade.topic ? ` · ${grade.topic}` : ''}${grade.title ? ` · ${grade.title}` : ''} — ${gradeLong(t, grade.value)}`}
      className={`inline-flex shrink-0 items-center justify-center rounded-[10px] px-1.5 leading-none font-semibold tabular-nums ${size === 'md' ? 'h-9 min-w-9 text-[16px]' : 'h-8 min-w-8 text-[15px]'}`}
      style={{ backgroundImage: grad(toneOf(grade)), color: INK }}
    >
      {gradeShort(t, grade.value, grade.special)}
    </span>
  );
}

/** Tiny trend line of 12-point grades, in the subject's ink. */
function Sparkline({ grades, color }: { grades: StudentGrade[]; color: string }) {
  const points = grades
    .filter((g) => g.value?.scale === 'points12')
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((g) => (g.value?.scale === 'points12' ? g.value.points : 0));
  if (points.length < 2) return null;
  const w = 72;
  const h = 26;
  const path = points
    .map(
      (p, i) =>
        `${((i / (points.length - 1)) * w).toFixed(1)},${(h - 2 - ((p - 1) / 11) * (h - 4)).toFixed(1)}`,
    )
    .join(' ');
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
      className="hidden shrink-0 overflow-visible md:block"
    >
      <polyline
        points={path}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** How many of each grade: twelve bars in the colours of the levels. */
function Spread({ grades }: { grades: StudentGrade[] }) {
  const counts = Array.from({ length: 12 }, () => 0);
  for (const g of grades) {
    if (g.value?.scale === 'points12')
      counts[g.value.points - 1] = (counts[g.value.points - 1] ?? 0) + 1;
  }
  const max = Math.max(1, ...counts);
  return (
    <div className="mt-6 grid grid-cols-12 gap-1.5">
      {counts.map((n, i) => {
        const tone = LEVEL[Math.min(3, Math.floor(i / 3))] as Grad;
        return (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <div className="flex h-20 w-full items-end">
              <span
                title={String(n)}
                className="block w-full rounded-[6px]"
                style={
                  n
                    ? {
                        height: `${Math.max(8, (n / max) * 100)}%`,
                        backgroundImage: `linear-gradient(180deg, ${GRAD[tone].to} 0%, ${GRAD[tone].from} 100%)`,
                      }
                    : { height: '8%', backgroundColor: PAPER }
                }
              />
            </div>
            <span className="text-[11px] font-medium" style={{ color: INK_FAINT }}>
              {i + 1}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** One subject: its picture, the grades, the trend, the average; opens to every grade. */
function SubjectRow({
  subject,
  grades,
  missed,
}: {
  subject: string;
  grades: StudentGrade[];
  missed: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const look = subjectLook(subject);
  const tone = SUBJECT_GRAD[look.tone];
  const average = pointsAverage(grades);
  return (
    <li style={{ borderTop: `1px solid ${LINE}` }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="group flex w-full flex-wrap items-center gap-x-4 gap-y-2 py-3 text-left"
      >
        <SubjectArt icon={look.icon} tone={tone} size={40} />
        <span className="min-w-0 flex-1 sm:w-44 sm:flex-none">
          <span className="block truncate text-[16px] leading-5 font-medium" style={{ color: INK }}>
            {subject}
          </span>
          {missed > 0 && (
            <span className="text-[12.5px]" style={{ color: INK_SOFT }}>
              {t('grades.absencesCount', { count: missed })}
            </span>
          )}
        </span>
        <span className="order-last flex min-w-0 basis-full flex-wrap gap-1.5 pl-14 sm:order-none sm:basis-0 sm:flex-1 sm:pl-0">
          {grades.map((g) => (
            <GradeBadge key={g.id} grade={g} />
          ))}
        </span>
        <Sparkline grades={grades} color={GRAD[tone].ink} />
        <span
          className="w-14 shrink-0 text-right font-display text-[28px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {decimal(average)}
        </span>
        <CaretDownIcon
          size={13}
          weight="bold"
          aria-hidden
          color={INK}
          className={`shrink-0 transition-transform duration-300 ease-spring ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul className="motion-rise flex flex-col gap-2 pb-4 pl-14">
          {[...grades].reverse().map((g) => (
            <li key={g.id} className="flex items-center gap-3 text-[13.5px]" style={{ color: INK }}>
              <GradeBadge grade={g} />
              <span className="min-w-0">
                {shortDate(g.date)} · {t(`journal.kinds.${g.kind}`)}
                <span style={{ color: INK_SOFT }}>
                  {g.topic ? ` · ${g.topic}` : g.title ? ` · ${g.title}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function GradesPage() {
  const ctx = useCurrentContext();
  const look = useLook();
  if (ctx.kind === 'student')
    return look === 'shapes2' ? (
      <ShapesGrades key={ctx.key} ctx={ctx} />
    ) : (
      <StudentGrades key={ctx.key} ctx={ctx} />
    );
  return <FamilyGradesPage />;
}

/** A small soft rectangle of the paper with a count. */
function Count({ children }: { children: string }) {
  return (
    <span
      className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] font-medium"
      style={{ backgroundColor: PAPER, color: INK }}
    >
      {children}
    </span>
  );
}

function FamilyGradesPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const terms = useTerms(ctx.schoolId);
  const [termId, setTermId] = useState<string>();
  const term =
    terms.data?.find((x) => x.id === termId) ?? (terms.data && termFor(terms.data, todayIso()));
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const record = useStudentRecord(child?.memberId ?? '', term?.id);

  const grades = record.data?.grades ?? [];
  const bySubject = new Map<string, StudentGrade[]>();
  for (const g of grades) bySubject.set(g.subject, [...(bySubject.get(g.subject) ?? []), g]);
  const subjects = [...bySubject.keys()].sort((a, b) => a.localeCompare(b, 'uk'));
  const absences = record.data?.absences ?? [];
  const formative = record.data?.formative ?? [];
  const average = pointsAverage(grades);

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('grades.title')}
        note={child ? [child.firstName, child.class?.label].filter(Boolean).join(' · ') : undefined}
      >
        {terms.data && terms.data.length > 1 && (
          <Tabs
            label={t('journal.term', { n: '' })}
            value={term?.id}
            onChange={setTermId}
            options={terms.data.map((x) => ({
              id: x.id,
              label: t('journal.term', { n: x.number }),
            }))}
          />
        )}
      </PageHead>

      {record.isPending && child ? (
        <Spinner label={t('common.loading')} />
      ) : record.isError ? (
        <EmptyState>{t('common.error')}</EmptyState>
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
          <div className="flex min-w-0 flex-col gap-4 max-lg:contents">
            <Block className="max-lg:order-1">
              <p className="text-[14px] font-medium" style={{ color: INK_SOFT }}>
                {t('grades.average')}
              </p>
              <p
                className="font-display text-[88px] leading-[92px] font-light tracking-[-0.05em]"
                style={{ color: INK }}
              >
                {decimal(average)}
              </p>
              <p className="mt-1 flex flex-wrap gap-1.5">
                <Count>{t('student.grades.count', { count: grades.length })}</Count>
                <Count>{t('grades.absencesCount', { count: absences.length })}</Count>
              </p>
              <Spread grades={grades} />
            </Block>

            {formative.length > 0 && (
              <Block className="max-lg:order-3">
                <Head title={t('grades.formative')} />
                <ul className="flex flex-col gap-4">
                  {formative.slice(0, 8).map((f) => {
                    const kind = (f.sticker && FEEDBACK[f.sticker]) || {
                      tone: 'lavender' as Grad,
                      icon: ChatCircleIcon,
                    };
                    const Icon = kind.icon;
                    return (
                      <li key={f.id} className="flex gap-3">
                        <span
                          className="flex size-9 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundImage: grad(kind.tone), color: GRAD[kind.tone].ink }}
                        >
                          <Icon size={16} weight="fill" aria-hidden />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                            {f.subject} · {dayMonth(f.date)}
                            {f.sticker &&
                              ` · ${t(`stickers.${f.sticker as 'star'}`).replace(/^[^\p{L}]+/u, '')}`}
                          </span>
                          {f.comment && (
                            <span
                              className="block text-[15px] leading-[22px]"
                              style={{ color: INK }}
                            >
                              {f.comment}
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </Block>
            )}

            <Block className="max-lg:order-4">
              <Head title={t('grades.absences')} count={absences.length} />
              {absences.length === 0 ? (
                <p className="text-[14px]" style={{ color: INK_SOFT }}>
                  {t('grades.noAbsences')}
                </p>
              ) : (
                <ul className="flex flex-col">
                  {absences.slice(0, 12).map((a) => {
                    const tone = ABSENCE[a.mark] ?? 'stone';
                    return (
                      <li
                        key={a.lessonId}
                        className="flex items-center gap-3 py-2"
                        style={{ borderTop: `1px solid ${LINE}` }}
                      >
                        <span
                          className="w-12 shrink-0 text-[13px] tabular-nums"
                          style={{ color: INK_SOFT }}
                        >
                          {dayMonth(a.date)}
                        </span>
                        <span
                          className="min-w-0 flex-1 truncate text-[14.5px]"
                          style={{ color: INK }}
                        >
                          {a.subject}
                        </span>
                        <span
                          className="shrink-0 rounded-[7px] px-2 py-1 text-[11.5px] leading-none font-medium"
                          style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
                        >
                          {t(`journal.absenceFull.${a.mark}`)}
                        </span>
                      </li>
                    );
                  })}
                  {absences.length > 12 && (
                    <li
                      className="pt-2 text-[13px]"
                      style={{ borderTop: `1px solid ${LINE}`, color: INK_FAINT }}
                    >
                      {t('studentHome.shapes2.moreTasks', { count: absences.length - 12 })}
                    </li>
                  )}
                </ul>
              )}
            </Block>
          </div>

          <Block className="max-lg:order-2">
            <Head title={t('grades.bySubject')} />
            {subjects.length === 0 ? (
              <p style={{ color: INK_SOFT }}>{t('grades.noGrades')}</p>
            ) : (
              <ul className="flex flex-col">
                {subjects.map((s) => (
                  <SubjectRow
                    key={s}
                    subject={s}
                    grades={[...(bySubject.get(s) ?? [])].sort((a, b) =>
                      a.date.localeCompare(b.date),
                    )}
                    missed={absences.filter((a) => a.subject === s).length}
                  />
                ))}
              </ul>
            )}
          </Block>
        </div>
      )}
    </div>
  );
}
